import { isFuture, isPast, subDays } from 'date-fns';
import { SortedCoaDocs } from './types';
import { partition } from 'lodash';
import { CoaProjectRaw } from '../../../types';
import { sortDocsByDate } from '../../../libs/sortDocsByDate';
import { parseDocumentDates } from './parseDocumentDates';
import { COA_NOTIFY_IN_DAYS } from '../config';

export function filterAndSortCOA(coaProjects: CoaProjectRaw[]): SortedCoaDocs {
  const processedCOA: SortedCoaDocs = {
    good: [],
    needAttention: [],
  };

  coaProjects.forEach((project) => {
    // if there is no document of project, add project without document to need attention and stop
    const { coaDocuments: coaDocsRaw, ...projectProps } = project;

    const coaDocuments = coaDocsRaw.map((d) => parseDocumentDates(d));

    if (coaDocuments.length === 0) {
      processedCOA.needAttention.push(projectProps);

      return;
    }

    const [futureDocs, restDocs] = partition(coaDocuments, (d) => d.startDate && isFuture(d.startDate));

    // all future documents should be in good section
    processedCOA.good.push(...futureDocs.map((document) => ({ ...projectProps, document })));

    // if there are no current or expired documents, add project without document to need attention and stop
    if (restDocs.length === 0) {
      processedCOA.needAttention.push(projectProps);

      return;
    }

    // Note! there could be several current documents, but this case is not covered, since it is rather the data issue
    const currentDoc = restDocs.find((d) => d.startDate && isPast(d.startDate) && d.endDate && isFuture(d.endDate));

    // if there is a current document, check the expiration period, place it to the proper section, return (current and future) and stop
    // Note! all of the rest (past and probably other current documents will be dropped, even if they marked as 'Fix requested')
    if (currentDoc && currentDoc.endDate) {
      const willExpireSoon = isPast(subDays(currentDoc.endDate, COA_NOTIFY_IN_DAYS));

      if (willExpireSoon) {
        processedCOA.needAttention.push({ ...projectProps, document: currentDoc });
      } else {
        processedCOA.good.push({ ...projectProps, document: currentDoc });
      }

      return;
    }

    // Here there could be only expired documents. Sort them and get one with the latest end date
    const sortedExpiredDocs = sortDocsByDate(restDocs);
    const theLatestExpiredDoc = sortedExpiredDocs[0];

    processedCOA.needAttention.push({ ...projectProps, document: theLatestExpiredDoc });
  });

  return processedCOA;
}
